import { useState } from 'react';
import { debounce } from 'lodash';

import { clampBallSize, DPDG_BALL_SIZE_MIN } from '../components/tools/dpdg/constants';

export type Trajectory =
  | 'horizontal'
  | 'vertical'
  | 'diagonal-bl-tr'
  | 'diagonal-br-tl'
  | 'circle'
  | 'eight-vertical'
  | 'eight-horizontal'
  | 'cross';

export const DPDG_TRAJECTORIES: Trajectory[] = [
  'horizontal',
  'vertical',
  'diagonal-bl-tr',
  'diagonal-br-tl',
  'circle',
  'eight-vertical',
  'eight-horizontal',
  'cross',
];

export const isTrajectory = (value: unknown): value is Trajectory =>
  typeof value === 'string' && DPDG_TRAJECTORIES.includes(value as Trajectory);

export const normalizeTrajectory = (value: Trajectory | string | undefined): Trajectory =>
  isTrajectory(value) ? value : 'horizontal';

interface IDpdgStateProps {
  initialTrajectory?: Trajectory;
  initialSpeed?: number;
  initialSize?: number | null;
  initialBackground?: string | null;
}

export const useDpdgState = (props: IDpdgStateProps = {}) => {
  const {
    initialTrajectory = 'horizontal',
    initialSpeed = 0.2,
    initialSize = DPDG_BALL_SIZE_MIN,
    initialBackground = '',
  } = props;

  const [trajectory, setTrajectory] = useState<Trajectory>(initialTrajectory);
  const [bgColor, setBgColor] = useState('#F3F4F6');
  const [ballColor, setBallColor] = useState('#3b65b9');
  const [velocity, setVelocity] = useState(initialSpeed);
  const [backgroundType, setBackgroundType] = useState<'none' | 'video' | 'image'>(
    initialBackground ? 'image' : 'none',
  );
  const [backgroundValue, setBackgroundValue] = useState(initialBackground || '');
  const [ballSize, setBallSizeState] = useState(() => clampBallSize(initialSize));

  const debouncedSetBgColor = debounce(setBgColor, 300);
  const debouncedSetBallColor = debounce(setBallColor, 300);
  const debouncedSetVelocity = debounce(setVelocity, 300);
  const debouncedSetBallSize = debounce((value: number) => setBallSizeState(clampBallSize(value)), 300);

  const reset = () => {
    setTrajectory(initialTrajectory);
    setBgColor('#F3F4F6');
    setBallColor('#3b65b9');
    setVelocity(initialSpeed);
    setBackgroundType(initialBackground ? 'image' : 'none');
    setBackgroundValue(initialBackground || '');
    setBallSizeState(clampBallSize(initialSize));
  };

  return {
    trajectory,
    setTrajectory,
    bgColor,
    ballColor,
    velocity,
    setBgColor: debouncedSetBgColor,
    setBallColor: debouncedSetBallColor,
    setVelocity: debouncedSetVelocity,
    setBallSize: debouncedSetBallSize,
    backgroundType,
    setBackgroundType,
    backgroundValue,
    setBackgroundValue,
    ballSize,
    reset,
  };
};
